import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import YumiiLogo from '../components/YumiiLogo';
import { db } from '../firebase/client';
import { parseDate, formatDateReadable } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { AppContext } from '../context/AppContext';
import { Restaurant, Dish } from '../types';
import { HeartIcon, StarIcon } from '../components/icons';
import { useData } from '../context/DataContext';
import { useNotifications } from '../context/NotificationContext';
import RestaurantCard from '../components/RestaurantCard';
import DishCard from '../components/DishCard';
import PackCard from '../components/PackCard';
import DishDetailModal from '../components/DishDetailModal';
import CartIcon from '../components/CartIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

// -- DONNÉES DES CATÉGORIES (ICÔNES & COULEURS) --
const CATEGORIES_DATA = [
    { name: 'Burgers', icon: '/assets/categories/Burger.png' },
    { name: 'Pizzas', icon: '/assets/categories/Pizza.png' },
    { name: 'Plats', icon: '/assets/categories/Plat.png' },
    { name: 'Traditionnel', icon: '/assets/categories/Traditionnel.png' },
    { name: 'Sandwichs', icon: '/assets/categories/Sandwich.png' },
    { name: 'Tacos', icon: '/assets/categories/Tacos.png' },
    { name: 'Salades', icon: '/assets/categories/Salades.png' },
    { name: 'Pasta', icon: '/assets/categories/Pasta.png' },
    { name: 'Poissons', icon: '/assets/categories/Poissons.png' },
    { name: 'Mexicain', icon: '/assets/categories/Mexicain.png' },
    { name: 'Entrée chaude', icon: '/assets/categories/Entrees_chaudes.png' },
    { name: 'Crisps', icon: '/assets/categories/Crisps.png' },
    { name: 'Desserts', icon: '/assets/categories/Desserts.png' },
];

// -- COMPOSANT : MODALE CATÉGORIE --
interface CategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    categoryName: string;
    onSelectRestaurant: (id: string) => void;
}



const CategoryModal: React.FC<CategoryModalProps> = ({ isOpen, onClose, categoryName, onSelectRestaurant }) => {
    const { restaurants, activeWilayas, fetchCategoryDishes, isFetchingCategory } = useData();

    // Load category dishes globally on first open
    useEffect(() => {
        if (isOpen && categoryName) {
            fetchCategoryDishes(categoryName);
        }
    }, [isOpen, categoryName, fetchCategoryDishes]);

    // Group dishes by Restaurant only (filtering by Wilaya applied first)
    const groupedRestaurants = useMemo(() => {
        if (!categoryName) return [];

        const groups: { restaurant: Restaurant, dishes: Dish[] }[] = [];

        restaurants.forEach(resto => {
            // Unify: only show approved restaurants (offline allowed now)
            if (resto.status !== 'approved') return;

            // Search category in menu (which should now be populated by fetchCategoryDishes)
            const cat = resto.menu.find(c => c.category === categoryName);
            if (cat && cat.dishes.length > 0) {
                groups.push({
                    restaurant: resto,
                    dishes: cat.dishes
                });
            }
        });
        
        // Sort: Online first
        return groups.sort((a, b) => {
            if (a.restaurant.isOnline && !b.restaurant.isOnline) return -1;
            if (!a.restaurant.isOnline && b.restaurant.isOnline) return 1;
            return 0;
        });

    }, [categoryName, restaurants]);

    const scrollRef = useRef<HTMLDivElement>(null);

    // Force window to top and lock body scroll to ensure fixed modal renders from the very top on iOS
    useEffect(() => {
        if (isOpen) {
            window.scrollTo({ top: 0, behavior: 'instant' });
            document.body.style.overflow = 'hidden';
            if (scrollRef.current) {
                scrollRef.current.scrollTop = 0;
            }
        } else {
            document.body.style.overflow = '';
        }

        return () => { document.body.style.overflow = ''; };
    }, [isOpen, categoryName]);

    if (!isOpen) return null;

    const hasData = groupedRestaurants.length > 0;

    const modalContent = (
        <div className="fixed inset-0 z-[100] flex flex-col bg-gray-50 animate-fade-in overflow-hidden">
            {/* Header Modale */}
            <div className="bg-red-600 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-6 px-4 shadow-[0_15px_30px_-10px_rgba(220,38,38,0.4)] rounded-b-[2rem] flex items-center justify-between z-20 sticky top-0">
                <div className="flex items-center gap-4">
                    <button onClick={onClose} className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl hover:bg-white/30 transition-colors shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div>
                        <h2 className="text-2xl font-black text-white flex items-center gap-2.5 tracking-tight">
                            <div 
                                className="w-10 h-10 bg-white"
                                style={{
                                    WebkitMaskImage: `url('${CATEGORIES_DATA.find(c => c.name === categoryName)?.icon}')`,
                                    WebkitMaskSize: 'contain',
                                    WebkitMaskPosition: 'center',
                                    WebkitMaskRepeat: 'no-repeat',
                                    maskImage: `url('${CATEGORIES_DATA.find(c => c.name === categoryName)?.icon}')`,
                                    maskSize: 'contain',
                                    maskPosition: 'center',
                                    maskRepeat: 'no-repeat'
                                }}
                            />
                            {categoryName}
                        </h2>
                        {hasData && (
                            <p className="text-xs text-red-100 font-bold mt-0.5 tracking-wide uppercase">
                                {groupedRestaurants.length} restaurant{groupedRestaurants.length > 1 ? 's' : ''} trouvé{groupedRestaurants.length > 1 ? 's' : ''}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Result Header - Just spacing since we removed Wilaya filters */}
            <div className="bg-gray-50 h-2 px-4 z-10"></div>

            {/* List of Restaurants with Horizontal Scroll */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto pb-20 bg-gray-50 overscroll-y-auto touch-pan-y">
                {isFetchingCategory ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin"></div>
                        <p className="mt-4 text-gray-500 font-bold uppercase tracking-widest text-xs">Recherche des {categoryName}...</p>
                    </div>
                ) : hasData ? (
                    <div className="space-y-8 pt-6 px-6 pb-24">
                        {groupedRestaurants.map((group) => (
                            <div key={group.restaurant.id} className="animate-fade-in block w-full border-b border-gray-100 last:border-0 pb-8">
                                {/* Restaurant Header */}
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-200 shadow-lg relative">
                                            <img src={group.restaurant.image} alt={group.restaurant.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div>
                                            <h4 className="font-extrabold text-gray-900 text-lg leading-tight flex items-center gap-2">
                                                {group.restaurant.name}
                                                {group.restaurant.deliveryEnabled === false && (
                                                    <span className="bg-red-600 text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase italic tracking-widest border border-red-700">À emporter</span>
                                                )}
                                                <span className="text-[9px] bg-red-600 text-white px-1.5 py-0.5 rounded uppercase font-black tracking-widest">
                                                    {group.restaurant.wilaya}
                                                </span>
                                            </h4>
                                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{group.dishes.length} PLATS</p>
                                        </div>
                                    </div>
                                    {/* Optional: Go to Restaurant Button */}
                                    <button 
                                        onClick={() => {
                                            onClose();
                                            onSelectRestaurant(group.restaurant.id);
                                        }}
                                        className="text-black text-[10px] font-black border-2 border-black px-4 py-1.5 rounded-lg active:scale-95 transition-all uppercase tracking-tighter"
                                    >
                                        Voir Menu
                                    </button>
                                </div>

                                {/* Horizontal Scroll of Dishes */}
                                <div className="flex overflow-x-auto gap-6 pb-4 -mx-6 px-6 no-scrollbar">
                                    {group.dishes.map((dish, idx) => (
                                        <div key={`${dish.id}-${idx}`} className="w-52 flex-shrink-0 relative group">
                                            <DishCard
                                                dish={dish}
                                                restaurantName={group.restaurant.name}
                                                restaurantId={group.restaurant.id}
                                                globalDiscount={group.restaurant.globalDiscount}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center opacity-60 mt-10 p-6">
                        <span className="text-6xl mb-4 grayscale opacity-50">🍽️</span>
                        <h3 className="text-lg font-bold text-gray-800">Aucun plat trouvé</h3>
                        <p className="text-gray-500 max-w-xs mx-auto mt-2">Nous n'avons pas trouvé de plats dans cette catégorie pour le moment.</p>
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

// -- COMPOSANT : CARTE EN VEDETTE (GÉANTE MODERNE AVEC EFFET 3D) --
const FeaturedRestaurantCard: React.FC<{
    restaurant: Restaurant & { isFavorite?: boolean };
    onClick: () => void;
    isActive: boolean;
}> = ({ restaurant, onClick, isActive }) => {
    return (
        <div
            onClick={onClick}
            className={`relative w-[80vw] md:w-[400px] h-[550px] flex-shrink-0 rounded-[2.5rem] overflow-hidden cursor-pointer snap-center transition-all duration-500 ease-out 
            ${isActive
                    ? 'scale-100 opacity-100 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] z-10'
                    : 'scale-[0.95] opacity-70 shadow-lg grayscale-[20%] z-0'
                } ${!restaurant.isOnline ? 'grayscale brightness-[0.7] opacity-90' : ''}`}
        >
            <img
                src={restaurant.image}
                alt={restaurant.name}
                className="absolute inset-0 w-full h-full object-cover"
            />

            {/* [NEW] "Fermé" Badge Overlay for Featured */}
            {!restaurant.isOnline && (
                <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/80 backdrop-blur-md px-10 py-4 rounded-[2rem] border-2 border-white/20 shadow-2xl transform -rotate-6 scale-125">
                        <span className="text-white text-xl font-black uppercase tracking-[0.3em] italic">Fermé</span>
                    </div>
                </div>
            )}

            <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-80'}`}></div>

            <div className="absolute top-6 left-6">
                <div className="flex items-center bg-white/20 backdrop-blur-md border border-white/30 text-white px-4 py-2 rounded-full shadow-lg">
                    <StarIcon className="w-5 h-5 mr-1 text-yellow-400" />
                    <span className="font-extrabold text-lg">{restaurant.rating}</span>
                </div>
            </div>

            {/* Top Right Badges */}
            <div className="absolute top-6 right-6 flex flex-col items-end gap-3 z-20">
                {restaurant.globalDiscount > 0 && (
                    <div className="bg-red-600 text-white text-lg font-bold px-4 py-2 rounded-2xl shadow-lg border-2 border-white/20">
                        -{restaurant.globalDiscount}%
                    </div>
                )}
                {restaurant.deliveryEnabled === false && (
                    <div className="bg-red-600 text-white text-[10px] font-black px-4 py-2 rounded-full shadow-lg border border-white/20 uppercase italic tracking-widest">
                        À emporter
                    </div>
                )}
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-8 pt-20">
                <div className={`transform transition-all duration-500 ${isActive ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-80'}`}>
                    <h3 className="text-white text-4xl font-extrabold tracking-tight drop-shadow-xl mb-3 leading-tight">
                        {restaurant.name}
                    </h3>
                    <p className="text-gray-200 text-lg font-medium mb-6 line-clamp-2 drop-shadow-md">
                        {restaurant.slogan}
                    </p>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center text-white text-sm bg-white/20 px-4 py-2 rounded-xl backdrop-blur-lg border border-white/10">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="font-bold uppercase tracking-wider">{restaurant.wilaya}</span>
                        </div>
                        <button className="bg-red-600 text-white px-6 py-2.5 rounded-full font-bold shadow-lg shadow-red-900/40 hover:bg-red-500 transition-colors">
                            Voir
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};



// -- COMPOSANT : CARTE PROMOTION --
const PromoDishCard: React.FC<{ dish: Dish; restaurantName: string; restaurantId: string; discount: number; onClick: () => void }> = ({ dish, restaurantName, discount, onClick }) => {
    const discountedPrice = Math.round(dish.price * (1 - discount / 100));

    return (
        <div
            onClick={onClick}
            className="w-56 flex-shrink-0 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 mr-4 cursor-pointer relative"
        >
            <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-md z-10 shadow-sm">
                -{discount}%
            </div>
            <div className="relative h-36">
                <img src={dish.image} alt={dish.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-3">
                <h4 className="font-bold text-gray-900 truncate">{dish.name}</h4>
                <p className="text-xs text-gray-500 truncate mb-2">{restaurantName}</p>
                <div className="flex items-end justify-between">
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-400 line-through font-medium">{dish.price} DA</span>
                        <span className="text-lg font-bold text-red-600">{discountedPrice} DA</span>
                    </div>
                    <button className="bg-red-50 text-red-700 p-1.5 rounded-full hover:bg-red-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

// -- ÉCRAN PRINCIPAL --
const HomeScreen: React.FC = () => {
    const { setView } = useContext(AppContext);
    const { currentUser, refreshProfile } = useAuth();
    const { loading, restaurants, error, refreshRestaurants, activeWilayas, ensureDishesLoaded, fetchPromoDishes, isFetchingPromos, ensurePacksLoaded } = useData();
    const [searchValue, setSearchValue] = useState("");
    const [showRetry, setShowRetry] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const preloadingRef = useRef<Set<string>>(new Set());

    const [activeIndex, setActiveIndex] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);
    const shuffleSeedRef = useRef(Math.random());
    const [shuffleTick, setShuffleTick] = useState(0);

    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
    const [showNotifications, setShowNotifications] = useState(false);

    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    // Nouvel état pour gérer l'ouverture directe d'un plat en promo
    type PromoData = { dish: Dish; restaurantName: string; restaurantId: string; discount: number, wilaya: string, rating: number };
    const [selectedPromo, setSelectedPromo] = useState<PromoData | null>(null);

    // Support for Android Hardware Back Button
    useEffect(() => {
        const handleHardwareBack = (e: Event) => {
            if (selectedCategory) {
                console.log("🛡️ [HomeScreen] Intercepted Back Button - Closing CategoryModal");
                setSelectedCategory(null);
                e.preventDefault();
            } else if (selectedPromo) {
                console.log("🛡️ [HomeScreen] Intercepted Back Button - Closing Promo Modal");
                setSelectedPromo(null);
                e.preventDefault();
            }
        };

        window.addEventListener('yumii:hardwareBack', handleHardwareBack);
        return () => window.removeEventListener('yumii:hardwareBack', handleHardwareBack);
    }, [selectedCategory, selectedPromo]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (loading) {
            timer = setTimeout(() => setShowRetry(true), 12000);
        } else {
            setShowRetry(false);
            // Once initial restaurants are loaded, fetch global promos
            fetchPromoDishes();
        }
        return () => clearTimeout(timer);
    }, [loading, fetchPromoDishes]);

    // Random shuffle interval every 20 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setShuffleTick(prev => prev + 1);
            shuffleSeedRef.current = Math.random();
        }, 20000);
        return () => clearInterval(interval);
    }, []);

    // Load packs for top restaurants to show on home screen
    useEffect(() => {
        if (!loading && restaurants.length > 0) {
            // Subscribe up to 20 restaurants to show their packs (featured first)
            const featured = restaurants.filter(r => r.featuredStatus === 'active' || r.featured);
            const others = restaurants.filter(r => !(r.featuredStatus === 'active' || r.featured));
            const targets = [...featured, ...others].slice(0, 20);
            
            targets.forEach(r => ensurePacksLoaded(r.id));
        }
    }, [loading, restaurants, ensurePacksLoaded]);

    useEffect(() => {
        const handleScroll = () => {
            // Seuil augmenté à 150px pour n'activer l'ombre du logo que quand la recherche a disparu
            setIsScrolled(window.scrollY > 150);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleHorizontalScroll = () => {
        if (!scrollRef.current) return;
        const container = scrollRef.current;

        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;

        let closestIndex = 0;
        let minDistance = Infinity;

        Array.from(container.children).forEach((child, index) => {
            if (index >= featuredRestaurants.length) return;

            const childRect = (child as HTMLElement).getBoundingClientRect();
            const childCenter = childRect.left + childRect.width / 2;
            const distance = Math.abs(containerCenter - childCenter);

            if (distance < minDistance) {
                minDistance = distance;
                closestIndex = index;
            }
        });

        if (closestIndex !== activeIndex) {
            setActiveIndex(closestIndex);
        }
    };

    const handleSelectRestaurant = (id: string) => {
        setView({ screen: 'restaurantDetail', params: { id } });
    };



    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setView({ screen: 'restaurants', params: { search: searchValue } });
    }

    const featuredRestaurants = useMemo(() => {
        let featured = restaurants.filter(r => (r.featuredStatus === 'active' || r.featured === true) && r.status === 'approved');

        // STRICT WILAYA FILTERING: Only show featured restaurants from user's current Wilaya
        if (currentUser?.wilaya) {
            featured = featured.filter(r => r.wilaya === currentUser.wilaya);
        }

        // Sort reliably based on id and a single random seed to prevent reshuffling on every simple re-render
        const seededRandom = (seed: number, id: string) => {
            let hash = Math.floor(seed * 100000);
            for (let i = 0; i < id.length; i++) {
                hash = Math.imul(31, hash) + id.charCodeAt(i) | 0;
            }
            return hash;
        };

        return featured.sort((a, b) => {
            // 1. Online first
            if (a.isOnline && !b.isOnline) return -1;
            if (!a.isOnline && b.isOnline) return 1;
            
            // 2. Random seeded sort
            return seededRandom(shuffleSeedRef.current, a.id) - seededRandom(shuffleSeedRef.current, b.id);
        });
    }, [restaurants, currentUser?.wilaya, shuffleTick]);

    const latestRestaurants = useMemo(() => {
        const sorted = restaurants.filter(r => r.status === 'approved').sort((a, b) => {
            // 1. Online first
            if (a.isOnline && !b.isOnline) return -1;
            if (!a.isOnline && b.isOnline) return 1;

            // 2. Creation date
            const dateA = parseDate(a.createdAt).getTime();
            const dateB = parseDate(b.createdAt).getTime();
            if (dateA !== 0 || dateB !== 0) return dateB - dateA;
            return -1;
        });

        // prioritize user's wilaya
        if (currentUser?.wilaya) {
            return [...sorted].sort((a, b) => {
                const aIsLocal = a.wilaya === currentUser.wilaya;
                const bIsLocal = b.wilaya === currentUser.wilaya;
                if (aIsLocal && !bIsLocal) return -1;
                if (!aIsLocal && bIsLocal) return 1;
                return 0;
            });
        }
        return sorted;
    }, [restaurants, currentUser?.wilaya]);

    const promoDishes = useMemo(() => {
        const promos: PromoData[] = [];
        restaurants.forEach(resto => {
            if (resto.status !== 'approved' || resto.isOnline === false) return;
            const globalDiscount = resto.globalDiscount || 0;
            if (resto.menu) {
                resto.menu.forEach(category => {
                    if (category.dishes) {
                        category.dishes.forEach(dish => {
                            const specificDiscount = dish.discount || 0;
                            const effectiveDiscount = specificDiscount > 0 ? specificDiscount : globalDiscount;
                            if (effectiveDiscount > 0) {
                                promos.push({
                                    dish,
                                    restaurantName: resto.name,
                                    restaurantId: resto.id,
                                    discount: effectiveDiscount,
                                    wilaya: resto.wilaya,
                                    rating: resto.rating
                                });
                            }
                        });
                    }
                });
            }
        });

        const sorted = promos.sort((a, b) => b.discount - a.discount);

        // Add secondary random factor based on shuffleTick
        const seededSort = (list: PromoData[]) => {
            const seed = shuffleSeedRef.current;
            return [...list].sort((a, b) => {
                if (a.discount !== b.discount) return b.discount - a.discount;
                // For same discount, use seeded random based on ID (dish.id + restId)
                const hashA = (a.dish.id + a.restaurantId).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) * seed;
                const hashB = (b.dish.id + b.restaurantId).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) * seed;
                return (hashA % 1) - (hashB % 1);
            });
        };

        const randomized = seededSort(promos);

        // Prioritize user's wilaya
        if (currentUser?.wilaya) {
            return [...randomized].sort((a, b) => {
                if (a.wilaya === currentUser.wilaya && b.wilaya !== currentUser.wilaya) return -1;
                if (a.wilaya !== currentUser.wilaya && b.wilaya === currentUser.wilaya) return 1;
                return 0;
            });
        }

        return randomized;
    }, [restaurants, currentUser?.wilaya, shuffleTick]);

    const allPacks = useMemo(() => {
        const packs: { pack: any, restaurant: Restaurant }[] = [];
        restaurants.forEach(resto => {
            if (resto.status !== 'approved' || resto.isOnline === false) return;
            if (resto.packs) {
                resto.packs.forEach(p => {
                    packs.push({ pack: p, restaurant: resto });
                });
            }
        });

        const sortedPacks = [...packs].sort((a, b) => {
            const seed = shuffleSeedRef.current;
            const hashA = (a.pack.id || a.pack.name).split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0) * seed;
            const hashB = (b.pack.id || b.pack.name).split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0) * seed;
            return (hashA % 1) - (hashB % 1);
        });

        // Filter by user wilaya if available
        if (currentUser?.wilaya) {
            return [...sortedPacks].sort((a, b) => {
                if (a.restaurant.wilaya === currentUser.wilaya && b.restaurant.wilaya !== currentUser.wilaya) return -1;
                if (a.restaurant.wilaya !== currentUser.wilaya && b.restaurant.wilaya === currentUser.wilaya) return 1;
                return 0;
            });
        }

        return sortedPacks;
    }, [restaurants, currentUser?.wilaya, shuffleTick]);

    const hasRestaurantsInWilaya = useMemo(() => {
        if (!currentUser?.wilaya) return true;
        return restaurants.some(r => r.wilaya === currentUser.wilaya);
    }, [restaurants, currentUser?.wilaya]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-white text-center p-6">
                <div className="w-12 h-12 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mb-6"></div>
                <p className="text-gray-500 font-medium">Chargement de Yumii...</p>
                {showRetry && (
                    <div className="mt-8 animate-fade-in">
                        <p className="text-gray-400 text-sm mb-4">La connexion semble lente (ou beaucoup de photos)...</p>
                        <button onClick={() => window.location.reload()} className="px-6 py-2 bg-gray-100 text-gray-700 font-bold rounded-full hover:bg-gray-200 transition-colors">Recharger la page</button>
                    </div>
                )}
            </div>
        );
    }

    if (error && restaurants.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-white p-8 text-center">
                <div className="bg-red-50 p-4 rounded-full mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Impossible de charger les données</h2>
                <p className="text-gray-500 mb-8 max-w-sm">{error}</p>
                <button onClick={refreshRestaurants} className="px-8 py-3 bg-red-600 text-white font-bold rounded-full hover:bg-red-700 shadow-lg transition-transform active:scale-95">Réessayer</button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            {/* Header Rouge (Logo + Notifs) */}
            <header className={`bg-red-600 px-6 pt-[calc(max(env(safe-area-inset-top),20px)+20px)] pb-1 sticky top-0 z-30 transition-shadow duration-300 ${isScrolled ? 'shadow-xl rounded-b-2xl pb-4' : ''}`}>
                <div className="flex justify-between items-center relative">
                    <div className="mb-1 flex items-center h-10">
                        <YumiiLogo className="h-full w-auto object-contain" />
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Notification Bell */}
                        <div className="relative">
                            <button
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-300"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                {unreadCount > 0 && (
                                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 transform translate-x-1/4 -translate-y-1/4 bg-red-800 rounded-full border border-white">
                                        {unreadCount}
                                    </span>
                                )}
                            </button>

                            {/* Notification Dropdown */}
                            <AnimatePresence>
                                {showNotifications && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                        className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl overflow-hidden z-50 origin-top-right border border-gray-100"
                                    >
                                        <div className="p-3 bg-red-50 border-b border-red-100 flex justify-between items-center">
                                            <h3 className="font-bold text-gray-800 text-sm">Notifications</h3>
                                            <button onClick={markAllAsRead} className="text-xs text-red-600 font-semibold hover:underline">Tout marquer lu</button>
                                        </div>
                                        <div className="max-h-80 overflow-y-auto">
                                            {notifications.length > 0 ? (
                                                notifications.map((notif: any) => (
                                                    <div
                                                        key={notif.id}
                                                        onClick={() => {
                                                            markAsRead(notif.id);
                                                            setShowNotifications(false);
                                                            if (notif.type === 'order_update') setView({ screen: 'account' });
                                                        }}
                                                        className={`p-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${notif.read ? 'opacity-60' : 'bg-red-50/30'}`}
                                                    >
                                                        <div className="flex justify-between items-start mb-1">
                                                            <h4 className={`text-sm ${notif.read ? 'font-medium text-gray-700' : 'font-bold text-gray-900'}`}>{notif.title}</h4>
                                                            <span className="text-[10px] text-gray-400 font-bold">{formatDateReadable(notif.timestamp)}</span>
                                                        </div>
                                                        <p className="text-xs text-gray-500 line-clamp-2">{notif.message}</p>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="p-8 text-center text-gray-400 text-sm">
                                                    Aucune notification
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Cart Icon */}
                        <CartIcon light={true} />
                    </div>
                </div>
            </header>

            {hasRestaurantsInWilaya ? (
                <>
                    {/* Contenu Défilant (Barre de recherche scrollable) */}
                    <div className="px-6 pt-0 pb-8 bg-red-600 rounded-b-[2rem] shadow-xl -mt-[1px] relative z-10">
                        <p className="text-red-100 text-base font-medium mb-3">Envie de quoi aujourd'hui ?</p>
                        <form onSubmit={handleSearch} className="relative transform transition-all focus-within:scale-[1.01]">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Restaurants, sushis, burgers..."
                                className="w-full py-3.5 pl-9 pr-4 bg-white rounded-xl text-gray-800 font-semibold placeholder-gray-400 focus:ring-2 focus:ring-white outline-none shadow-sm transition-all"
                                value={searchValue}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
                            />
                        </form>
                    </div>

                    {/* 3. En Vedette (Cartes Géantes 3D) */}
                    {featuredRestaurants.length > 0 && (
                        <section className="mt-8 mb-4 relative z-10">
                            <div className="flex flex-col px-6 mb-4">
                                <h2 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic">En vedette</h2>
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] italic mt-0">Trending cette semaine</p>
                            </div>

                            <div
                                ref={scrollRef}
                                onScroll={handleHorizontalScroll}
                                className="flex overflow-x-auto gap-5 px-[10vw] pb-16 pt-4 no-scrollbar snap-x snap-mandatory"
                            >
                                {featuredRestaurants.map((restaurant, index) => (
                                    <FeaturedRestaurantCard
                                        key={restaurant.id}
                                        restaurant={restaurant}
                                        isActive={index === activeIndex}
                                        onClick={() => handleSelectRestaurant(restaurant.id)}
                                    />
                                ))}
                                <div className="w-[5vw] flex-shrink-0"></div>
                            </div>
                        </section>
                    )}

                    {/* 4. RAIL DES CATÉGORIES (HORIZONTAL) */}
                    <section className="mb-10 pl-6">
                        <h2 className="text-xl font-black text-gray-900 mb-4 tracking-tighter uppercase italic">Nos Spécialités</h2>
                        <div className="flex overflow-x-auto space-x-4 pr-6 pb-4 no-scrollbar">
                            {CATEGORIES_DATA.map((cat, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedCategory(cat.name)}
                                    className="flex flex-col items-center justify-center flex-shrink-0 group w-[100px] h-[120px] transition-all transform hover:scale-105 active:scale-95 px-2"
                                >
                                    <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mb-3 shadow-sm group-hover:bg-red-700 transition-colors">
                                        <div 
                                            className="w-12 h-12 bg-white"
                                            style={{
                                                WebkitMaskImage: `url('${cat.icon}')`,
                                                WebkitMaskSize: 'contain',
                                                WebkitMaskPosition: 'center',
                                                WebkitMaskRepeat: 'no-repeat',
                                                maskImage: `url('${cat.icon}')`,
                                                maskSize: 'contain',
                                                maskPosition: 'center',
                                                maskRepeat: 'no-repeat'
                                            }}
                                        />
                                    </div>
                                    <span className="text-[10px] font-black text-gray-900 text-center leading-tight uppercase tracking-tighter italic">{cat.name}</span>
                                </button>
                            ))}
                        </div>
                    </section>

                    {/* 4.5 SECTION PACKS (Gourmet Deals) */}
                    {allPacks.length > 0 && (
                        <section className="mb-10 pl-6 overflow-hidden">
                            <div className="flex items-center justify-between pr-6 mb-6">
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">
                                        Nouveaux Packs
                                    </h2>
                                    <p className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] italic mt-1">
                                        Le plein de saveurs à prix réduit
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                     <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></div>
                                     <div className="w-2 h-2 rounded-full bg-red-200"></div>
                                </div>
                            </div>
                            
                            <div className="flex overflow-x-auto gap-6 pr-6 pb-8 no-scrollbar snap-x snap-proximity">
                                {allPacks.map(({ pack, restaurant }, idx) => (
                                    <div key={`${pack.id}-${idx}`} className="snap-start first:pl-0">
                                        <PackCard 
                                            pack={pack} 
                                            restaurantName={restaurant.name}
                                            onPress={() => handleSelectRestaurant(restaurant.id)}
                                        />
                                    </div>
                                ))}
                                <div className="w-10 flex-shrink-0"></div>
                            </div>
                        </section>
                    )}

                    {/* 5. Plats en Promotion */}
                    {(promoDishes.length > 0 || isFetchingPromos) && (
                        <section className="mb-8 bg-white py-6 shadow-inner">
                            <div className="px-6 mb-4">
                                <h2 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">Offres Spéciales</h2>
                                <p className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] italic mt-1">Profitez des meilleures réductions</p>
                            </div>
                            {isFetchingPromos && promoDishes.length === 0 ? (
                                <div className="flex px-6 overflow-x-auto no-scrollbar gap-4">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="w-56 h-64 bg-gray-100 rounded-xl animate-pulse flex-shrink-0"></div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex overflow-x-auto px-6 pb-4 no-scrollbar gap-5">
                                    {promoDishes.map((item, idx) => (
                                        <div key={`${item.dish.id}-promo-${idx}`}>
                                            <DishCard
                                                dish={item.dish}
                                                restaurantName={item.restaurantName}
                                                restaurantId={item.restaurantId}
                                                globalDiscount={item.discount}
                                                onPress={() => setSelectedPromo(item)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}

                    {/* 6. Tous les restaurants */}
                    <section className="mb-8 px-6">
                        <h2 className="text-xl font-black text-gray-900 mb-4 tracking-tighter uppercase italic">Dernières découvertes</h2>
                        {latestRestaurants.length > 0 ? (
                            <div className="flex overflow-x-auto space-x-4 pb-4 no-scrollbar">
                                {latestRestaurants.map(resto => (
                                    <div key={`all-${resto.id}`} className="w-48 flex-shrink-0">
                                        <RestaurantCard restaurant={resto} onClick={() => handleSelectRestaurant(resto.id)} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6 bg-white rounded-xl border border-gray-100">
                                <p className="text-gray-500">Aucun restaurant disponible pour le moment.</p>
                            </div>
                        )}
                    </section>
                </>
            ) : (
                <div className="flex-1 flex flex-col items-center justify-center py-20 px-10 text-center animate-fade-in">
                    <div className="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-8 shadow-inner relative">
                        <div className="absolute inset-0 bg-red-100 rounded-full animate-ping opacity-20"></div>
                        <span className="text-6xl relative z-10">📍</span>
                    </div>
                    
                    <h2 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic mb-4 leading-tight">
                        Bientôt chez vous !
                    </h2>
                    
                    <p className="text-gray-500 font-bold text-sm uppercase tracking-wide max-w-xs mx-auto mb-10 leading-relaxed italic">
                        Malheureusement, il n'y a aucun restaurant Yumii dans votre wilaya <span className="text-red-600">({currentUser?.wilaya || 'sélectionnée'})</span> pour le moment.
                        <br/><br/>
                        Repassez bientôt, nous arrivons vite !
                    </p>
                    
                    <button 
                        onClick={refreshRestaurants}
                        className="px-10 py-4 bg-red-600 text-white font-black rounded-2xl shadow-xl shadow-red-200 hover:bg-red-700 active:scale-95 transition-all uppercase tracking-widest text-sm italic"
                    >
                        Réessayer
                    </button>
                    
                    <div className="mt-12 flex items-center gap-2 opacity-30 grayscale">
                        <YumiiLogo className="h-6 w-auto" />
                    </div>
                </div>
            )}


            {/* MODALE DETAILS CATÉGORIE */}
            {selectedCategory && (
                <CategoryModal
                    isOpen={!!selectedCategory}
                    onClose={() => setSelectedCategory(null)}
                    categoryName={selectedCategory}
                    onSelectRestaurant={handleSelectRestaurant}
                />
            )}

            {/* MODALE DETAIL PLAT EN PROMOTION */}
            {selectedPromo && (
                <DishDetailModal
                    dish={selectedPromo.dish}
                    restaurantName={selectedPromo.restaurantName}
                    restaurantId={selectedPromo.restaurantId}
                    globalDiscount={selectedPromo.discount}
                    onClose={() => setSelectedPromo(null)}
                    restaurantWilaya={selectedPromo.wilaya}
                    restaurantRating={selectedPromo.rating}
                />
            )}

        </div>
    );
};

export default HomeScreen;